/**
 * Client Matrix Dashboard Route
 *
 * Executive-level client profitability dashboard with:
 * - Hero KPIs (Receita, Custos, Margem, Clients, Jobs)
 * - Period comparison (vs Previous Period / vs Same Period Last Year)
 * - Interactive charts (Evolution, Composition)
 * - Granularity-based table (Accumulated, Yearly, Quarterly, Monthly)
 * - Client-aggregated table with drill-down links
 *
 * Route: /_auth/financial/client-matrix/
 *
 * @see docs/research/trpc/frontend/src/routes/client-matrix/index.tsx - Reference implementation
 */

import { trpc } from '@/api/trpc';
import {
  ClientMatrixChart,
  ClientMatrixWaterfall,
  type ClientChartDataPoint,
  type ClientChartType,
} from '@/app/financial/client-matrix/components';
import {
  DREControlsPanel,
  calculateComparisonRange,
  calculatePeriodDates,
  type ComparisonMode,
  type DREGranularity,
  type PeriodDates,
} from '@/app/financial/dre/components';
import { getMarginCellStyles } from '@/app/financial/shared/constants';
import {
  KPICard,
  BottomActionSheet,
  FloatingActionButton,
  type ActionItem,
} from '@/app/financial/dre/drill-down/components';
import { TableSparkline } from '@/app/financial/shared/components';
import { useClientSparklineData } from '@/app/financial/shared/hooks';
import { useFilterSync } from '@/app/financial/shared/hooks/useFilterSync';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================

// Valid granularity values for URL validation
const VALID_GRANULARITIES: DREGranularity[] = ['accumulated', 'yearly', 'quarterly', 'monthly'];

// ============================================================================
// TYPES
// ============================================================================

// Client aggregation from backend (with added percentOfTotal for frontend)
interface ClientAggregation {
  codigoCliente: string;
  nomeCliente: string;
  receita: number;
  custos: number;
  margem: number;
  margemPercent: number;
  notasCount: number;
  jobsCount: number;
  percentOfTotal: number;
}

// KPI definitions
interface KPIDefinition {
  key: string;
  label: string;
  icon: string;
  color: string;
  getValue: (totals: Record<string, number>) => number;
  format?: 'currency' | 'number' | 'percent';
  getSubtitle?: (value: number, totals: Record<string, number>) => string;
}

const KPI_DEFINITIONS: KPIDefinition[] = [
  {
    key: 'receita',
    label: 'Receita',
    icon: '',
    color: '#D2FA52',
    getValue: (t) => t['receita'] || 0,
  },
  {
    key: 'custos',
    label: 'Custos',
    icon: '',
    color: '#ff6b6b',
    getValue: (t) => t['custos'] || 0,
  },
  {
    key: 'margem',
    label: 'Margem',
    icon: '',
    color: '#D2FA52',
    getValue: (t) => t['margem'] || 0,
    getSubtitle: (value, totals) => {
      const receita = totals['receita'] || 0;
      return receita > 0 ? `${((value / receita) * 100).toFixed(1)}%` : '';
    },
  },
  {
    key: 'clientes',
    label: 'Clientes',
    icon: '',
    color: '#9c27b0',
    getValue: (t) => t['clientes'] || 0,
    format: 'number',
  },
  {
    key: 'jobs',
    label: 'Jobs',
    icon: '',
    color: '#ff9800',
    getValue: (t) => t['jobs'] || 0,
    format: 'number',
  },
  {
    key: 'notas',
    label: 'Notas',
    icon: '',
    color: '#607d8b',
    getValue: (t) => t['notas'] || 0,
    format: 'number',
  },
];

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

// Route definition - useFilterSync handles URL state management
export const Route = createFileRoute('/_auth/financial/client-matrix/')({
  component: ClientMatrixDashboard,
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCompactCurrency(value: number): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function getDefaultDateRange(): PeriodDates {
  return calculatePeriodDates('ytd');
}

function getQuarter(month: number): number {
  return Math.ceil(month / 3);
}

// Aggregate monthly chart data by granularity
function aggregateChartData(
  monthlyData: ClientChartDataPoint[],
  granularity: DREGranularity
): ClientChartDataPoint[] {
  if (granularity === 'monthly') {
    return monthlyData;
  }

  if (granularity === 'accumulated') {
    // Sum all months into a single point
    const totals = monthlyData.reduce(
      (acc, m) => ({
        receita: acc.receita + m.receita,
        custos: acc.custos + m.custos,
        margem: acc.margem + m.margem,
      }),
      { receita: 0, custos: 0, margem: 0 }
    );
    return [{
      period: 'total',
      label: 'Acumulado',
      ...totals,
    }];
  }

  if (granularity === 'yearly') {
    // Group by year
    const byYear: Record<string, { receita: number; custos: number; margem: number }> = {};
    monthlyData.forEach(m => {
      const year = m.period.split('-')[0];
      if (!byYear[year]) byYear[year] = { receita: 0, custos: 0, margem: 0 };
      byYear[year].receita += m.receita;
      byYear[year].custos += m.custos;
      byYear[year].margem += m.margem;
    });
    return Object.entries(byYear)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([year, data]) => ({
        period: year,
        label: year,
        ...data,
      }));
  }

  if (granularity === 'quarterly') {
    // Group by quarter
    const byQuarter: Record<string, { receita: number; custos: number; margem: number }> = {};
    monthlyData.forEach(m => {
      const [year, monthStr] = m.period.split('-');
      const quarter = getQuarter(parseInt(monthStr, 10));
      const key = `${year}-Q${quarter}`;
      if (!byQuarter[key]) byQuarter[key] = { receita: 0, custos: 0, margem: 0 };
      byQuarter[key].receita += m.receita;
      byQuarter[key].custos += m.custos;
      byQuarter[key].margem += m.margem;
    });
    return Object.entries(byQuarter)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, data]) => {
        const [year, q] = key.split('-');
        return {
          period: key,
          label: `${q}/${year}`,
          ...data,
        };
      });
  }

  return monthlyData;
}

// ============================================================================
// LOADING SKELETON (Monutrack Dark Theme)
// ============================================================================

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="p-8 min-h-screen">
      {/* Header skeleton */}
      <div className="mb-6">
        <div className="w-[250px] h-9 animate-pulse bg-brand-dark-light rounded-lg mb-2" />
        <div className="w-[400px] h-5 animate-pulse bg-brand-dark-light rounded" />
      </div>
      {/* KPI cards skeleton */}
      <div className="flex gap-4 mb-6 flex-wrap">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="flex-1 h-[100px] animate-pulse bg-brand-dark-light rounded-xl" />
        ))}
      </div>
      {/* Table skeleton */}
      <div className="w-full h-[400px] animate-pulse bg-brand-dark-light rounded-xl" />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function ClientMatrixDashboard() {
  const navigate = useNavigate();
  const defaultDates = getDefaultDateRange();

  // Use filter sync for URL-backed state management (FR6, FR7, FR8 - PRD Week 50)
  const { state: filterState, setFilter, setFilters } = useFilterSync({
    defaults: {
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      granularity: 'accumulated', // Default to accumulated (FR7 - matching BU Matrix pattern)
      comparisonMode: 'none',
      chartType: 'bar',
      selectedDimensions: [],
    },
  });

  // Extract filter values for convenience
  const { startDate, endDate, granularity, comparisonMode } = filterState;

  // Local state for non-URL-backed state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'receita', desc: true }]);

  // Selected metrics for chart visibility
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(() =>
    new Set(['receita', 'custos', 'margem'])
  );

  // Active chart tab
  const [activeChart, setActiveChart] = useState<'evolution' | 'composition'>('evolution');

  // Chart type for evolution chart
  const [evolutionChartType, setEvolutionChartType] = useState<ClientChartType>('combo');

  // Mobile action sheet state
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);

  // Toggle metric visibility
  const toggleMetric = useCallback((metricKey: string) => {
    setSelectedMetrics(prev => {
      const next = new Set(prev);
      if (next.has(metricKey)) {
        next.delete(metricKey);
      } else {
        next.add(metricKey);
      }
      return next;
    });
  }, []);

  // Calculate comparison date range
  const comparisonRange = useMemo(
    () => calculateComparisonRange(startDate, endDate, comparisonMode),
    [startDate, endDate, comparisonMode]
  );

  // Fetch client matrix data (aggregated by backend)
  const {
    data: matrixResult,
    isLoading: isLoadingMatrix,
    error: matrixError,
    refetch: refetchMatrix,
  } = trpc.dre.getClientMatrix.useQuery(
    {
      dataInicial: new Date(startDate),
      dataFinal: new Date(endDate),
    },
    {
      enabled: !!startDate && !!endDate,
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Fetch comparison client matrix data
  const {
    data: comparisonMatrixResult,
  } = trpc.dre.getClientMatrix.useQuery(
    {
      dataInicial: comparisonRange ? new Date(comparisonRange.startDate) : new Date(),
      dataFinal: comparisonRange ? new Date(comparisonRange.endDate) : new Date(),
    },
    {
      enabled: !!comparisonRange,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  const isLoading = isLoadingMatrix;
  const error = matrixError;

  const refetch = useCallback(() => {
    void refetchMatrix();
  }, [refetchMatrix]);

  // Raw monthly data from backend
  const rawMonthlyData: ClientChartDataPoint[] = useMemo(() => {
    if (!matrixResult?.monthly) return [];
    return matrixResult.monthly.map(m => ({
      period: m.period ?? '',
      label: m.label ?? '',
      receita: m.receita ?? 0,
      custos: m.custos ?? 0,
      margem: m.margem ?? 0,
    }));
  }, [matrixResult]);

  // Chart data aggregated by granularity
  const chartData: ClientChartDataPoint[] = useMemo(() => {
    return aggregateChartData(rawMonthlyData, granularity);
  }, [rawMonthlyData, granularity]);

  // Raw comparison monthly data
  const rawComparisonMonthlyData: ClientChartDataPoint[] | undefined = useMemo(() => {
    if (!comparisonRange || !comparisonMatrixResult?.monthly) return undefined;
    return comparisonMatrixResult.monthly.map(m => ({
      period: m.period ?? '',
      label: m.label ?? '',
      receita: m.receita ?? 0,
      custos: m.custos ?? 0,
      margem: m.margem ?? 0,
    }));
  }, [comparisonRange, comparisonMatrixResult]);

  // Comparison chart data aggregated by granularity
  const comparisonChartData: ClientChartDataPoint[] | undefined = useMemo(() => {
    if (!rawComparisonMonthlyData) return undefined;
    return aggregateChartData(rawComparisonMonthlyData, granularity);
  }, [rawComparisonMonthlyData, granularity]);

  // Client aggregations from backend (add percentOfTotal)
  const clientAggregations = useMemo((): ClientAggregation[] => {
    if (!matrixResult?.clients) return [];
    const totalReceita = matrixResult.totals?.receita ?? 0;
    return matrixResult.clients.map(c => ({
      codigoCliente: c.codigoCliente ?? '',
      nomeCliente: c.nomeCliente ?? '',
      receita: c.receita ?? 0,
      custos: c.custos ?? 0,
      margem: c.margem ?? 0,
      margemPercent: c.margemPercent ?? 0,
      notasCount: c.notasCount ?? 0,
      jobsCount: c.jobsCount ?? 0,
      percentOfTotal: totalReceita > 0 ? ((c.receita ?? 0) / totalReceita) * 100 : 0,
    }));
  }, [matrixResult]);

  // Client sparkline data for trend visualization
  const { getData: getSparklineData } = useClientSparklineData(matrixResult?.monthlyByClient);

  // Comparison aggregations from backend
  const comparisonAggregations = useMemo((): Map<string, ClientAggregation> => {
    if (!comparisonRange || !comparisonMatrixResult?.clients) return new Map();
    const totalReceita = comparisonMatrixResult.totals?.receita ?? 0;
    const map = new Map<string, ClientAggregation>();
    comparisonMatrixResult.clients.forEach(c => {
      const codigoCliente = c.codigoCliente ?? '';
      map.set(codigoCliente, {
        codigoCliente,
        nomeCliente: c.nomeCliente ?? '',
        receita: c.receita ?? 0,
        custos: c.custos ?? 0,
        margem: c.margem ?? 0,
        margemPercent: c.margemPercent ?? 0,
        notasCount: c.notasCount ?? 0,
        jobsCount: c.jobsCount ?? 0,
        percentOfTotal: totalReceita > 0 ? ((c.receita ?? 0) / totalReceita) * 100 : 0,
      });
    });
    return map;
  }, [comparisonRange, comparisonMatrixResult]);

  // Totals from backend
  const totals = useMemo((): Record<string, number> => {
    if (!matrixResult?.totals) {
      return { receita: 0, custos: 0, margem: 0, clientes: 0, jobs: 0, notas: 0 };
    }
    return {
      receita: matrixResult.totals.receita ?? 0,
      custos: matrixResult.totals.custos ?? 0,
      margem: matrixResult.totals.margem ?? 0,
      clientes: matrixResult.totals.clientsCount ?? 0,
      jobs: matrixResult.totals.jobsCount ?? 0,
      notas: clientAggregations.reduce((s, c) => s + c.notasCount, 0),
    };
  }, [matrixResult, clientAggregations]);

  // Comparison totals from backend
  const comparisonTotals = useMemo((): Record<string, number> | null => {
    if (!comparisonRange || !comparisonMatrixResult?.totals) return null;
    const compClients = Array.from(comparisonAggregations.values());
    return {
      receita: comparisonMatrixResult.totals.receita ?? 0,
      custos: comparisonMatrixResult.totals.custos ?? 0,
      margem: comparisonMatrixResult.totals.margem ?? 0,
      clientes: comparisonMatrixResult.totals.clientsCount ?? 0,
      jobs: comparisonMatrixResult.totals.jobsCount ?? 0,
      notas: compClients.reduce((s, c) => s + c.notasCount, 0),
    };
  }, [comparisonRange, comparisonMatrixResult, comparisonAggregations]);

  // Calculate delta
  const calcDelta = (current: number, comparison: number | undefined): number | null => {
    if (comparison === undefined || comparison === 0) return null;
    return ((current - comparison) / Math.abs(comparison)) * 100;
  };

  // Handle drill-down navigation
  const handleDrillDown = useCallback((codigoCliente: string) => {
    void navigate({
      to: '/financial/client-matrix/$clientId',
      params: { clientId: codigoCliente },
      search: { startDate, endDate, granularity },
    });
  }, [navigate, startDate, endDate, granularity]);

  // Handle CSV export
  const handleExportCSV = useCallback(() => {
    const headers = ['Cliente', 'Codigo', 'Notas', 'Jobs', 'Receita', 'Custos', 'Folha', 'Imposto', 'Despesa', 'Margem', 'Margem %', '% Total'];
    const rows = clientAggregations.map(c => [
      c.nomeCliente,
      c.codigoCliente,
      c.notasCount,
      c.jobsCount,
      c.receita.toFixed(2),
      c.custos.toFixed(2),
      '', // Folha - placeholder
      '', // Imposto - placeholder
      '', // Despesa - placeholder
      c.margem.toFixed(2),
      c.margemPercent.toFixed(2),
      c.percentOfTotal.toFixed(2),
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `matriz-clientes-${startDate}-${endDate}.csv`;
    link.click();
  }, [clientAggregations, startDate, endDate]);

  // Mobile action handlers
  const handleShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Matriz de Clientes - Monutrack',
          url: window.location.href,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copiado para a area de transferencia');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast.error('Erro ao compartilhar');
      }
    }
  }, []);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copiado para a area de transferencia');
    } catch {
      toast.error('Erro ao copiar link');
    }
  }, []);

  const handleGoBack = useCallback(() => {
    void navigate({ to: '/financial' });
  }, [navigate]);

  // Mobile action items
  const mobileActions: ActionItem[] = useMemo(() => [
    {
      label: 'Exportar CSV',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      onClick: handleExportCSV,
    },
    {
      label: 'Compartilhar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      ),
      onClick: handleShare,
    },
    {
      label: 'Copiar Link',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      ),
      onClick: handleCopyLink,
    },
    {
      label: 'Voltar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      ),
      onClick: handleGoBack,
    },
  ], [handleExportCSV, handleShare, handleCopyLink, handleGoBack]);

  // Column helper
  const columnHelper = createColumnHelper<ClientAggregation>();

  // Build columns
  const columns = useMemo(() => [
    columnHelper.accessor('nomeCliente', {
      header: 'Cliente',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div
            onClick={() => handleDrillDown(row.codigoCliente)}
            className="cursor-pointer text-brand-primary font-medium hover:underline"
            title={`Codigo: ${row.codigoCliente}`}
          >
            {info.getValue()}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'sparkline',
      header: () => <div className="text-center">Tendência</div>,
      cell: (info) => {
        const clientId = info.row.original.codigoCliente;
        const monthlyData = getSparklineData(clientId);
        return (
          <div className="flex justify-center">
            <TableSparkline data={monthlyData} />
          </div>
        );
      },
    }),
    columnHelper.accessor('notasCount', {
      header: () => <div className="text-right">Notas</div>,
      cell: (info) => <div className="text-right text-brand-gray-light">{info.getValue()}</div>,
    }),
    columnHelper.accessor('jobsCount', {
      header: () => <div className="text-right">Jobs</div>,
      cell: (info) => <div className="text-right text-brand-gray-light">{info.getValue()}</div>,
    }),
    columnHelper.accessor('receita', {
      header: () => <div className="text-right">Receita</div>,
      cell: (info) => {
        const row = info.row.original;
        const compRow = comparisonAggregations.get(row.codigoCliente);
        const delta = calcDelta(row.receita, compRow?.receita);

        return (
          <div className="text-right">
            <div className="text-brand-primary font-medium">{formatCurrency(info.getValue())}</div>
            {delta !== null && (
              <div className={`text-xs ${delta > 0 ? 'text-success' : delta < 0 ? 'text-brand-red' : 'text-brand-gray'}`}>
                {delta > 0 ? '▲' : delta < 0 ? '▼' : ''}{delta > 0 ? '+' : ''}{delta.toFixed(1)}%
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('custos', {
      header: () => <div className="text-right">Custos</div>,
      cell: (info) => (
        <div className="text-right text-brand-red">
          {formatCurrency(info.getValue())}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'folha',
      header: () => <div className="text-right">Folha</div>,
      cell: () => (
        <div className="text-right text-brand-gray">
          -
        </div>
      ),
    }),
    columnHelper.display({
      id: 'imposto',
      header: () => <div className="text-right">Imposto</div>,
      cell: () => (
        <div className="text-right text-brand-gray">
          -
        </div>
      ),
    }),
    columnHelper.display({
      id: 'despesa',
      header: () => <div className="text-right">Despesa</div>,
      cell: () => (
        <div className="text-right text-brand-gray">
          -
        </div>
      ),
    }),
    columnHelper.accessor('margem', {
      header: () => <div className="text-right">Margem</div>,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className={`text-right font-semibold ${value > 0 ? 'text-success' : value < 0 ? 'text-brand-red' : 'text-brand-gray'}`}>
            {formatCurrency(value)}
          </div>
        );
      },
    }),
    columnHelper.accessor('margemPercent', {
      header: () => <div className="text-right">Margem %</div>,
      cell: (info) => {
        const value = info.getValue();
        const styles = getMarginCellStyles(value);
        return (
          <div
            className={`${styles.backgroundColor} ${styles.textColor} text-right py-1 px-2 rounded-${styles.borderRadius} font-medium`}
          >
            {formatPercent(value)}
          </div>
        );
      },
    }),
    columnHelper.accessor('percentOfTotal', {
      header: () => <div className="text-right">% Total</div>,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="flex items-center gap-2 justify-end">
            <div className="w-[60px] h-2 bg-brand-gray/20 rounded overflow-hidden">
              <div
                className="h-full bg-brand-primary rounded"
                style={{ width: `${Math.min(value, 100)}%` }}
              />
            </div>
            <span className="text-sm text-brand-gray-light min-w-[40px]">
              {formatPercent(value)}
            </span>
          </div>
        );
      },
    }),
  ], [columnHelper, handleDrillDown, comparisonAggregations, getSparklineData]);

  const table = useReactTable({
    data: clientAggregations,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Render
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          Matriz de Clientes
        </h1>
        <p className="text-[#838998] font-body text-lg">
          Dashboard de rentabilidade por cliente - receitas, custos e margens
        </p>
      </div>

      {/* Controls Panel - Reusing DRE component with useFilterSync (FR6, FR7, FR8) */}
      <DREControlsPanel
        startDate={startDate}
        endDate={endDate}
        onPeriodChange={(dates) => {
          setFilters({ startDate: dates.startDate, endDate: dates.endDate });
        }}
        onStartDateChange={(date) => setFilter('startDate', date)}
        onEndDateChange={(date) => setFilter('endDate', date)}
        onRefresh={refetch}
        isLoading={isLoading}
        comparisonMode={comparisonMode}
        onComparisonModeChange={(mode) => setFilter('comparisonMode', mode)}
        comparisonRange={comparisonRange}
        granularity={granularity}
        onGranularityChange={(gran) => setFilter('granularity', gran)}
      />

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-brand-red/20 text-brand-red rounded-lg border border-brand-red/30">
          Erro: {error.message}
        </div>
      )}

      {clientAggregations.length > 0 ? (
        <>
          {/* KPI Cards - Click to toggle chart visibility */}
          <div className="flex gap-3 flex-wrap">
            {KPI_DEFINITIONS.map((kpi) => {
              const value = kpi.getValue(totals);
              const compValue = comparisonTotals ? kpi.getValue(comparisonTotals) : undefined;
              const change = calcDelta(value, compValue);

              let subtitle: string | undefined;
              if (kpi.getSubtitle) {
                subtitle = kpi.getSubtitle(value, totals);
              }

              const formattedValue = kpi.format === 'number'
                ? value.toLocaleString('pt-BR')
                : formatCompactCurrency(value);

              // Determine animation format based on KPI type
              const animationFormat = kpi.format === 'number' ? 'number' : 'compact-currency';

              return (
                <KPICard
                  key={kpi.key}
                  label={kpi.label}
                  value={formattedValue}
                  change={change}
                  subtitle={subtitle}
                  icon={kpi.icon}
                  active={selectedMetrics.has(kpi.key)}
                  onClick={() => toggleMetric(kpi.key)}
                  numericValue={value}
                  animationFormat={animationFormat}
                />
              );
            })}
          </div>
          <p className="text-xs text-brand-gray-light -mt-2 italic">
            Clique nos cards acima para mostrar/ocultar metricas nos graficos
          </p>

          {/* Chart Tabs */}
          <div>
            {/* Tab Navigation */}
            <div className="flex gap-0 border-b-2 border-brand-gray/20 mb-4">
              {[
                { id: 'evolution' as const, label: 'Evolucao Mensal' },
                { id: 'composition' as const, label: 'Composicao' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveChart(tab.id)}
                  className={`px-6 py-3 text-sm font-medium ${
                    activeChart === tab.id
                      ? 'bg-brand-primary text-brand-dark font-semibold border-b-2 border-brand-primary -mb-0.5'
                      : 'text-brand-gray hover:text-white border-b-2 border-transparent'
                  } rounded-t-lg transition-all duration-150 cursor-pointer`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Chart Content */}
            <div className="bg-brand-light/10 rounded-lg p-6 min-h-[300px]">
              {activeChart === 'evolution' && (
                <div>
                  {/* Chart Type Selector */}
                  <div className="flex justify-end mb-3 gap-1">
                    {[
                      { id: 'bar' as const, label: 'Barras' },
                      { id: 'line' as const, label: 'Linhas' },
                      { id: 'combo' as const, label: 'Combo' },
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setEvolutionChartType(type.id)}
                        className={`px-3 py-1.5 text-xs rounded border transition-all duration-150 cursor-pointer ${
                          evolutionChartType === type.id
                            ? 'border-brand-primary bg-brand-primary/10 text-brand-primary font-semibold'
                            : 'border-brand-gray/20 bg-brand-dark-light text-brand-gray font-normal hover:text-white'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                  <ClientMatrixChart
                    data={chartData}
                    comparisonData={comparisonChartData}
                    selectedMetrics={selectedMetrics}
                    chartType={evolutionChartType}
                    height={280}
                  />
                </div>
              )}

              {activeChart === 'composition' && (
                <ClientMatrixWaterfall
                  values={totals}
                  selectedMetrics={selectedMetrics}
                  height={300}
                />
              )}
            </div>
          </div>

          {/* Client Table */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-base text-white font-semibold">
                Clientes por Rentabilidade
                <span className="text-xs text-brand-gray font-normal ml-2">
                  ({clientAggregations.length} clientes)
                </span>
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleExportCSV}
                  className="btn-primary px-4 py-2 text-sm"
                >
                  Exportar CSV
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="table-financial w-full text-sm">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`py-3 px-2 border-b-2 border-brand-primary bg-brand-dark-light text-brand-gray whitespace-nowrap ${
                            header.id === 'nomeCliente' ? 'text-left' : 'text-right'
                          } ${header.column.getCanSort() ? 'cursor-pointer' : ''}`}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={row.index % 2 === 0 ? 'bg-brand-dark' : 'bg-brand-dark-light'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="p-2 border-b border-brand-dark-light"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {/* Totals Row */}
                <tfoot>
                  <tr className="bg-brand-primary/10 font-bold">
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-white">
                      TOTAL ({clientAggregations.length} clientes)
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary">
                      {/* Empty cell for sparkline column */}
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-brand-gray-light">
                      {totals.notas}
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-brand-gray-light">
                      {totals.jobs}
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-brand-primary">
                      {formatCurrency(totals.receita)}
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-brand-red">
                      {formatCurrency(totals.custos)}
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-brand-gray">
                      -
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-brand-gray">
                      -
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-brand-gray">
                      -
                    </td>
                    <td className={`py-3 px-2 border-t-2 border-brand-primary text-right ${totals.margem > 0 ? 'text-success' : 'text-brand-red'}`}>
                      {formatCurrency(totals.margem)}
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-white">
                      {totals.receita > 0 ? formatPercent((totals.margem / totals.receita) * 100) : '-'}
                    </td>
                    <td className="py-3 px-2 border-t-2 border-brand-primary text-right text-white">
                      100%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      ) : (
        <p className="text-center text-brand-gray mt-8">
          Nenhum dado encontrado para o periodo selecionado.
        </p>
      )}

      {/* Mobile FAB and Action Sheet */}
      <FloatingActionButton
        onClick={() => setIsActionSheetOpen(true)}
        ariaLabel="Abrir menu de acoes"
      />
      <BottomActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        title="Acoes"
        actions={mobileActions}
      />
    </div>
  );
}

export default ClientMatrixDashboard;
